import { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';

import { formatarValorEmReais } from '../../../shared/dominio/moeda';
import { EstadoDeCarregamento } from '../../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../../shared/ui/EstadoVazio';
import { opcoesDosItens } from '../api/contratos';
import { CONFERENCIAS_DO_ITEM, ROTULOS_DE_CONFERENCIA, type ConferenciaDoItem } from '../api/tipos';
import { PaginacaoDaTabela } from './PaginacaoDaTabela';
import { useBuscaPaginada } from './useBuscaPaginada';

/**
 * Itens vigentes do contrato (`TP-049`, T8/T9): busca por código ou descrição, filtrados pela competência
 * da tela — com a página de origem de cada um e se foi conferido (`TP-067`, `D8`).
 */
export function ItensDoContrato({
  contratoId,
  competencia,
  conferenciaInicial = '',
}: {
  contratoId: string;
  competencia: string;
  conferenciaInicial?: ConferenciaDoItem | '';
}): React.JSX.Element {
  const [conferencia, definirConferencia] = useState<ConferenciaDoItem | ''>(conferenciaInicial);
  const filtro = useBuscaPaginada();

  const consulta = useQuery(
    opcoesDosItens(contratoId, competencia, filtro.busca, filtro.pagina + 1, filtro.tamanho, conferencia),
  );

  return (
    <Stack spacing={1.5}>
      <Typography variant="h3">Itens do contrato</Typography>

      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
        <TextField
          label="Buscar por código ou descrição"
          size="small"
          value={filtro.digitado}
          onChange={(evento) => {
            filtro.definirDigitado(evento.target.value);
          }}
          helperText={filtro.aviso}
          sx={{ minWidth: 260 }}
        />
        <TextField
          select
          label="Conferência"
          size="small"
          value={conferencia}
          onChange={(evento) => {
            definirConferencia(evento.target.value as ConferenciaDoItem | '');
            filtro.voltarParaPrimeiraPagina();
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {CONFERENCIAS_DO_ITEM.map((opcao) => (
            <MenuItem key={opcao} value={opcao}>
              {ROTULOS_DE_CONFERENCIA[opcao]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        Lido com segurança: o extrator provou a unidade de cobrança. Conferido na revisão: o extrator não
        conseguiu provar a unidade, e uma pessoa conferiu o item antes de ativar o contrato.
      </Typography>

      {consulta.isPending ? <EstadoDeCarregamento linhas={4} /> : null}

      {consulta.isError ? (
        <EstadoDeErro
          erro={consulta.error}
          contexto="Não foi possível carregar os itens deste contrato"
          aoReprocessar={() => {
            void consulta.refetch();
          }}
        />
      ) : null}

      {consulta.isSuccess && consulta.data.total === 0 ? (
        <EstadoVazio
          titulo="Nenhum item nesta competência"
          descricao={
            filtro.busca === '' && conferencia === ''
              ? 'Este contrato ainda não tem itens ativos nesta competência. Os itens aparecem depois que uma fonte é ativada na revisão.'
              : 'Nenhum item combina com o filtro.'
          }
        />
      ) : null}

      {consulta.isSuccess && consulta.data.total > 0 ? (
        <TableContainer>
          <Table aria-label="Itens do contrato" size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Unidade de cobrança</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Página</TableCell>
                <TableCell>Conferência</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {consulta.data.itens.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.codigo}</TableCell>
                  <TableCell>{item.descricao}</TableCell>
                  <TableCell>{item.unidadeDeCobranca}</TableCell>
                  <TableCell>{formatarValorEmReais(item.valor)}</TableCell>
                  <TableCell>{item.pagina}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: item.conferencia === 'nao_conferido' ? 'warning.main' : undefined }}>
                    {ROTULOS_DE_CONFERENCIA[item.conferencia]}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <PaginacaoDaTabela total={consulta.data.total} busca={filtro} />
        </TableContainer>
      ) : null}
    </Stack>
  );
}
